/**
 * The instant the Atlas draws: the live clock, or a season/sol scrub's
 * preview riding over it, and the play, forecast and sol-scrub state the
 * panels, the tick and the probe read and drive.
 */

import { gameClock } from '../world/clock.ts';
import type { InjectsHandle } from './injects.ts';

export interface ViewClockDeps {
  readonly injects: InjectsHandle;
}

export interface ViewClockState {
  previewT: number | null;
  playing: 'sol' | 'year' | null;
  readonly clockRunning: boolean;
  forecasting: boolean;
  forecastAnchorT: number;
  solScrubbing: boolean;
}

export interface ViewClockHandle {
  viewT(): number;
  viewRate(): number;
  readonly viewClock: { readonly t: number; scrub(deltaSeconds: number): void; setInstant(t: number): void };
  beginPreview(): void;
  readonly state: ViewClockState;
}

export function createViewClock(deps: ViewClockDeps): ViewClockHandle {
  const { SCRUB_MOVES_CLOCK } = deps.injects.state;

  /**
   * The instant a season or sol scrub, or a sweep, is previewing; null is the
   * live clock. The Atlas draws this date while the clock the ship flies on
   * keeps running under it, and letting go returns the view to that clock.
   */
  let previewT: number | null = null;
  /** The instant the Atlas draws: the preview's, or the clock's. */
  const viewT = (): number => previewT ?? gameClock().t;
  /** Game seconds `viewT` moves a real second: 0 under a preview or a pause. */
  const viewRate = (): number => (previewT !== null || gameClock().paused ? 0 : gameClock().rate);
  /** What the scrubs move: the preview while there is one, the clock otherwise. */
  const viewClock = {
    get t(): number { return viewT(); },
    scrub(deltaSeconds: number): void {
      if (previewT === null) gameClock().scrub(deltaSeconds);
      else previewT += deltaSeconds;
    },
    setInstant(t: number): void {
      if (previewT === null) gameClock().setInstant(t);
      else previewT = t;
    },
  };
  /** Start previewing from the live clock, unless a preview is already up. */
  function beginPreview(): void {
    if (!SCRUB_MOVES_CLOCK && previewT === null) previewT = gameClock().t;
  }

  // 'sol' sweeps one Martian day; 'year' sweeps the seasons. They are different
  // animations because they cost different things — see the tick loop.
  let playing: 'sol' | 'year' | null = null;

  /**
   * Whether the plot follows the clock's travel each frame (src/main.ts
   * advances it). The clock ALWAYS responds to explicit scrub/setInstant.
   */
  let clockRunning = true;

  /**
   * Forecast scrub state. While the player drags the Ls slider the view
   * previews the scrubbed season (`previewT`), and letting go returns it to
   * the live clock.
   */
  let forecasting = false;
  /** The clock's t when the forecast scrub began, for `?scrubmovesclock=1`'s snap back. */
  let forecastAnchorT = 0;
  /** True while the player drags the SOL slider, which previews the same way. */
  let solScrubbing = false;

  return {
    viewT,
    viewRate,
    viewClock,
    beginPreview,
    state: {
      get previewT() { return previewT; },
      set previewT(v) { previewT = v; },
      get playing() { return playing; },
      set playing(v) { playing = v; },
      get clockRunning() { return clockRunning; },
      get forecasting() { return forecasting; },
      set forecasting(v) { forecasting = v; },
      get forecastAnchorT() { return forecastAnchorT; },
      set forecastAnchorT(v) { forecastAnchorT = v; },
      get solScrubbing() { return solScrubbing; },
      set solScrubbing(v) { solScrubbing = v; },
    },
  };
}
